import React, { FC, useEffect, useRef, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../components/Table'
import { Button, Checkbox, Container, FormControl, FormControlLabel, Grid, InputLabel, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import * as StatsAPI from '../../api/statistics/statistics'
import * as StatsModels from '../../api/statistics/models'
import * as ShortAPI from '../../api/short/index'
import * as ShortModels from '../../api/short/models'
import moment from "moment"
import "moment/locale/da"
import daLocale from "date-fns/locale/da"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import APIRequest from '../../components/helpers/APIRequest'
import * as CSV from '../../utils/exportcsv'
import LoadingSymbols from "../../utils/loadingSymbol"
import { Autocomplete } from '@material-ui/lab'
import * as mobilbase from '../../api/mobilbase';

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const Statistics: FC = () => {
    const classes = useStyles()

    const [currentLocation, setCurrentLocation] = useState(-1)
    const [locations, setLocations] = useState<ShortModels.Location[]>([])
    const [isPendingLocations, setIsPendingLocations] = useState(false)
    const [runLocations, setRunLocations] = useState(true)

    const [cardPaymentData, setCardPaymentData] = useState<StatsModels.CardPaymentData>(new StatsModels.CardPaymentData())
    const [isPending, setIsPending] = useState(false)
    const [run, setRun] = useState(false)
    const [showError, setShowError] = useState(false)
    const [fromDate, setFromDate] = useState<Date | null>(new Date())
    const [toDate, setToDate] = useState<Date | null>(new Date())
    const [fetchData, setFetchData] = useState(false)
    const [downloadData, setDownloadData] = useState(false)

    const [currentType, setCurrentType] = useState<number>(0)
    const [showOtherCardTypes, setShowOtherCardTypes] = useState(false)

    const [pendingLoadingPicture, setPendingLoadingPicture] = useState(false)

    // Necessary for avoiding strikethrough on the location combobox
    const labelLocationRef = useRef(null)
    const labelTypeRef = useRef(null)
    // @ts-ignore: Object is possibly 'null'
    const labelLocationWidth = (labelLocationRef !== null && labelLocationRef.current !== null) ? labelLocationRef.current.clientWidth : 0
    // @ts-ignore: Object is possibly 'null'
    const labelTypeWidth = (labelTypeRef !== null && labelTypeRef.current !== null) ? labelTypeRef.current.clientWidth : 0

    var keyList = [""]

    useEffect(() => {
        function handleKeyDown(event: any){
            const keyname = event.key
            keyList.push(keyname)
    
            if (event.shiftKey && keyList.includes("S") && keyList.includes("E")) {
                setDownloadData(true)
            }
            if (event.shiftKey && keyList.includes("S") && keyList.includes("H") ) {
                setFetchData(true)
            }
        }
        document.addEventListener("keydown", handleKeyDown)

        return function cleanUP() {
            document.removeEventListener("keydown", handleKeyDown)
        }
    })
    
    useEffect(() => {
        function handleKeyUp(event: any){
            keyList = [""]
            setDownloadData(false)
            setFetchData(false)
        }
        document.addEventListener("keyup", handleKeyUp)

        return function cleanUP() {
            document.removeEventListener("keyup", handleKeyUp)
        }
    })

    useEffect( () => {
        if (downloadData){
            downloadCSV()
        }
    }, [downloadData])

    useEffect( () => {
        if (fetchData){
            return setRun(true)
        }
    }, [fetchData])

    useEffect(() => {
        if (locations.length > 0 && currentLocation === -1) {
            setCurrentLocation(locations[0].id)
        }
    }, [locations, currentLocation])

    function compareNumericString(rowA: any, rowB: any, id: any, desc: any) {
        let a = parseFloat(rowA.values[id].replace(".", ""))
        let b = parseFloat(rowB.values[id].replace(".", ""))

        if (rowA.values[0] === "Total") {  // find total row, and put it last or first
            a = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
        }
        if (rowB.values[0] === "Total") {
            b = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
        }
        if (a > b) return 1; 
        if (a < b) return -1;
        return 0;
    }

    function compareDateString(rowA: any, rowB: any) {
        let a = rowA.original.emptyTime 
        let b = rowB.original.emptyTime

        if (a > b) return 1; 
        if (a < b) return -1;
        return 0;
    }

    function findSorter(index: number){

        if (index === 0){ // date is always 0
            return compareDateString
        }
        else{
            return compareNumericString
        }

    }

    const dynamicColumns: Column<Array<string|number>>[] = React.useMemo(() => {
        var headers: Array<{Header: string, accessor: (e: Array<string|number>) => string, id: string, sortType: any}> = []
        var count = cardPaymentData.result.headers.length
        if (count < 1) {
            return [{Header: "Dato", accessor: e => ""}, {Header: "Dankort", accessor: e => "" }, {Header: "Visa/Dankort", accessor: e => ""}, {Header: "Mastercard", accessor: e => ""}, {Header: "Total", accessor: e => ""}]
        }
        // Create a dynamic list of headers
        cardPaymentData.result.headers.forEach((_, index) => {
                headers.push({ Header: cardPaymentData.result.headers[index], accessor: e => "" + e[index], id: "" + index, sortType: findSorter(index) })
        });
        return headers
    }, [cardPaymentData])

    const dynamicColumnsOther: Column<Array<string|number>>[] = React.useMemo(() => {
        var headers: Array<{Header: string, accessor: (e: Array<string|number>) => string, id: string}> = []
        var count = cardPaymentData.result.otherCardHeaders.length
        if (count < 1) {
            return [{Header: "Dato", accessor: e => ""}, {Header: "OK", accessor: e => ""}, {Header: "Q8", accessor: e => ""}, {Header: "Total", accessor: e => ""}]
        }
        // Create a dynamic list of headers
        cardPaymentData.result.otherCardHeaders.forEach((element, index) => {
            headers.push({ Header: cardPaymentData.result.otherCardHeaders[index], accessor: e => "" + e[index], id: "" + index})
        });
        return headers
    }, [cardPaymentData])

    const RequestData = () => {
        const fixDate = (date: Date | null, startOfDay: boolean) => {
            // We have to send the date with a UTC timezone, or the server will get the value wrong
            var d1 = date ? date : new Date()
            if(startOfDay)var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 0, 0, 0, 0))
            else var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 23, 59, 59, 0))
            
            return d
        }
        const param = (): StatsModels.Params => {
            // Choose all devices if none are chosen
            return {location: currentLocation, body: {Start: fixDate(fromDate, true), End: fixDate(toDate, false), Meters: [], ShowOtherCards: showOtherCardTypes}}
        }

        return (<APIRequest<StatsModels.CardPaymentData>
            run={run} setRun={setRun}
            pending={isPending} setIsPending={setIsPending}
            req={() => StatsAPI.getStatsCardPayment(Number(currentType), param())}
            setData={setCardPaymentData}
            setShowError={setShowError}
        />)
    }

    const RequestLocationsData = () => {
        return (<APIRequest<ShortModels.Location[]>
            run={runLocations} setRun={setRunLocations}
            pending={isPendingLocations} setIsPending={setIsPendingLocations}
            req={ShortAPI.getLocations}
            setData={setLocations}
        />)
    }

    const handleTypeChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const type = event.target.value as number
        if (type !== currentType) {
            setCurrentType(type)
            setCardPaymentData(new StatsModels.CardPaymentData())
        }
    }

    const XlsxParam =():StatsModels.ParamsXlsx =>{
        const fixDate = (date: Date | null,  startOfDay: boolean) => {
            // We have to send the date with a UTC timezone, or the server will get the value wrong
            var d1 = date ? date : new Date()
            if(startOfDay)var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 0, 0, 0, 0));
            else var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 23, 59, 59, 0));
            return d
        }
        return {
            location:currentLocation,
            body:{
                Start: fixDate(fromDate, true), 
                End: fixDate(toDate, false),
                Meters: [0],
                Columns1:dynamicColumns,
                Columns2:dynamicColumnsOther,
                ShowOtherCards:showOtherCardTypes,
                TransactionType:Number(currentType),
            }
        }

    }

    const downloadCSV = async () => {
        //CSV.exportCSV("korttyper.csv", cardPaymentData.result.headers, cardPaymentData.result.rows, [0], [])
        setPendingLoadingPicture(true) // start loading symbol
        await mobilbase.requestXlsx("korttyper.xlsx","cardtypes",XlsxParam())
        .then((res) => {
            setPendingLoadingPicture(false) // ends loading symbol
        })
        .catch((err) =>{
            setPendingLoadingPicture(false) // end loading symbol
            setShowError(true) // if something fails, start error message
        })    
    }

    function updateLocValue(val: any){ // the method that the currentlocation gets updated from the Autocomplete
        let updatedLocValue = val?.id

        if (updatedLocValue === null){
            return false
        }
        else{
        setCurrentLocation(updatedLocValue)
        return true
        }
    }

    return (
        <Container maxWidth={false}>
            <LoadingSymbols pendingLoadingPicture={pendingLoadingPicture} setPendingLoadingPicture={pendingLoadingPicture} open={pendingLoadingPicture} loadingSymbolText={"Henter data, vent venligst..."} />
            <RequestData />
            <RequestLocationsData />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Kortbetaling</Typography>

                <Container maxWidth={"lg"} className={classes.margTop}>
                    <MuiPickersUtilsProvider locale={daLocale} utils={DateFnsUtils}>
                        <Grid container spacing={1} alignItems="center" justify="center">
                            <Grid item xs={6}>
                            <FormControl variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelLocationRef} shrink htmlFor="cardTypes-location"></InputLabel>
                                    <Autocomplete id="id" options={locations} getOptionLabel={option => option.name} onChange={(event, value) => updateLocValue(value)} renderInput={params => ( <TextField {...params} label="Pladser" variant="outlined" />  )}/>
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelTypeRef} shrink htmlFor="current-stat-input">Type</InputLabel>
                                    <Select labelWidth={labelTypeWidth} id="current-stat-input" value={currentType} onChange={handleTypeChange}>
                                        <MenuItem key={0} value={0}>Omsætning</MenuItem>
                                        <MenuItem key={1} value={1}>Antal</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <KeyboardDatePicker fullWidth disableToolbar variant="inline" inputVariant="outlined" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Fra dato" value={fromDate} onChange={setFromDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={2}>
                                <KeyboardDatePicker fullWidth disableToolbar variant="inline" inputVariant="outlined" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Til dato" value={toDate} onChange={setToDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={3}>
                                <FormControlLabel control={<Checkbox name="show_others" id="show_others" label="Vis benzinkort" checked={showOtherCardTypes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setShowOtherCardTypes(e.currentTarget.checked)}} />} label="Vis benzinkort" />
                            </Grid>
                           
                            <Grid item xs={4}>
                                <Button variant="contained" color="primary" className={classes.buts} onClick={() => setRun(true)}>Hent</Button>
                            </Grid>
                            <Grid item xs={4}>
                                <Button disabled={cardPaymentData.result.rows.length < 1} variant="contained" color="primary" className={classes.buts} onClick={downloadCSV}>Eksportér</Button>
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                </Container>
                <div className={classes.grid} >
                    <Table<Array<string|number>>  columns={dynamicColumns} data={cardPaymentData.result.rows} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                </div>
                <div className={classes.grid} hidden={!showOtherCardTypes}>
                    <Table<Array<string|number>> columns={dynamicColumnsOther} data={cardPaymentData.result.otherCardRows} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                </div>
            </div>
            <NoDataDialog open={showError} onClose={() => { setShowError(false) }} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>
        </Container>
    )
}

export default Statistics
